import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FocusTrap } from 'focus-trap-react';
import { cn } from '@/utils/cn';
import { Icon } from '../Icon';
import type { IconName } from '../Icon';
import { Button } from '../Button';
import { Typography } from '../Typography';
import { Checkbox } from '../Checkbox';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ModalButtonConfig {
  label: string;
  /**
   * Called when the button is clicked.
   * If `showDoNotShowAgain` is true, receives the current checkbox value.
   */
  onClick: (doNotShowAgain?: boolean) => void;
  /** Shows a spinner and disables the button (e.g. while a mutation is pending). */
  isLoading?: boolean;
  /** Disables the button without a spinner. */
  disabled?: boolean;
}

interface ModalBaseProps {
  /** Controls whether the modal is visible. */
  isOpen: boolean;
  /** Called when the user closes the modal (X button, Escape key, or backdrop click). */
  onClose: () => void;
  /**
   * Presentation style.
   * - `center` (default): centered dialog that scales in.
   * - `bottom-sheet`: full-width sheet anchored to the bottom that slides up,
   *   with a drag-handle affordance and rounded top corners.
   */
  variant?: 'center' | 'bottom-sheet';
  /** Primary action button — rendered first (top). */
  primaryButton?: ModalButtonConfig;
  /** Secondary action button — rendered below the primary. */
  secondaryButton?: ModalButtonConfig;
  /**
   * When true, renders a "Don't show this again" checkbox above the buttons.
   * The current checkbox value is passed as an argument to both button onClick handlers.
   */
  showDoNotShowAgain?: boolean;
  /**
   * Accessible name for the dialog, used in children mode where there is no
   * prop-driven title to label it. Without this, screen readers announce an
   * unnamed dialog. Ignored in prop-driven (title) mode, which labels the
   * dialog via its rendered title (`aria-labelledby`).
   */
  ariaLabel?: string;
}

/** Prop-driven mode: renders icon, title and description from props. */
interface ModalPropsWithContent extends ModalBaseProps {
  /**
   * Optional icon rendered in a circular `bg-informative` container above the title.
   * Must be a valid Primer IconName.
   */
  icon?: IconName;
  /** Modal heading. Rendered as Typography header/h1. */
  title: string;
  /** Supporting copy below the title. Rendered as Typography body/medium. */
  description: string;
  children?: never;
}

/** Children mode: renders arbitrary content. icon/title/description are not used. */
interface ModalPropsWithChildren extends ModalBaseProps {
  children: React.ReactNode;
  icon?: never;
  title?: never;
  description?: never;
}

export type ModalProps = ModalPropsWithContent | ModalPropsWithChildren;

// ─── Animation variants ───────────────────────────────────────────────────────

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.15, ease: 'easeOut' as const } },
  exit: { opacity: 0, transition: { duration: 0.15, ease: 'easeIn' as const } },
};

const panelVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.2, ease: 'easeOut' as const } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15, ease: 'easeIn' as const } },
};

const bottomSheetVariants = {
  hidden: { y: '100%' },
  visible: { y: 0, transition: { duration: 0.25, ease: 'easeOut' as const } },
  exit: { y: '100%', transition: { duration: 0.2, ease: 'easeIn' as const } },
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer Modal component.
 *
 * Renders into a React Portal attached to `document.body`. Visibility is
 * controlled via the `isOpen` prop — the component is fully unmounted when
 * closed (after the exit animation completes).
 *
 * Two content modes:
 * - **Prop-driven** (`title` + `description`): enforces the canonical modal
 *   layout with an optional icon, heading, and description copy.
 * - **Children** (`children`): renders arbitrary content inside the padded,
 *   chrome-enforced container. The close button and button row are still present.
 *
 * The close button, Escape key, and backdrop click all call `onClose`. Focus is
 * trapped inside the modal while it is open.
 *
 * @example
 * // Prop-driven
 * <Modal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   icon="heart"
 *   title="Save your progress?"
 *   description="You have unsaved changes. Save now to keep your work."
 *   primaryButton={{ label: 'Save', onClick: handleSave }}
 *   secondaryButton={{ label: 'Discard', onClick: handleDiscard }}
 *   showDoNotShowAgain
 * />
 *
 * @example
 * // Children mode
 * <Modal isOpen={isOpen} onClose={handleClose}>
 *   <p>Custom content goes here.</p>
 * </Modal>
 */
export function Modal({
  isOpen,
  onClose,
  variant = 'center',
  primaryButton,
  secondaryButton,
  showDoNotShowAgain = false,
  ariaLabel,
  ...rest
}: ModalProps) {
  const isBottomSheet = variant === 'bottom-sheet';
  const titleId = useId();
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Reset checkbox state each time the modal opens
  useEffect(() => {
    if (isOpen) setDoNotShowAgain(false);
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const hasTitle = 'title' in rest && rest.title !== undefined;
  const hasChildren = 'children' in rest && rest.children !== undefined;

  const handlePrimary = () => {
    primaryButton?.onClick(showDoNotShowAgain ? doNotShowAgain : undefined);
  };

  const handleSecondary = () => {
    secondaryButton?.onClick(showDoNotShowAgain ? doNotShowAgain : undefined);
  };

  const hasButtons = primaryButton != null || secondaryButton != null;

  const modal = (
    <AnimatePresence>
      {isOpen && (
        <FocusTrap
          focusTrapOptions={{
            initialFocus: () => closeButtonRef.current ?? undefined,
            allowOutsideClick: true,
          }}
        >
          {/* Backdrop — fixed inset-0 so it covers the full viewport */}
          <motion.div
            className={cn(
              'fixed inset-0 z-9999 flex',
              isBottomSheet ? 'items-end justify-center' : 'items-center justify-center p-4'
            )}
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Semi-transparent overlay — click to close */}
            <div className="absolute inset-0 bg-scrim" onClick={onClose} aria-hidden="true" />

            {/* Modal panel */}
            {/* overflow-hidden clips content to rounded-large corners.
                Do NOT put overflow-y-auto here — overscroll bounce on the panel itself
                breaks the clipping effect. Scrolling lives on the inner wrapper instead. */}
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby={hasTitle ? titleId : undefined}
              aria-label={hasTitle ? undefined : ariaLabel}
              className={cn(
                'relative z-10 w-full',
                // overflow-hidden clips content to rounded corners — do NOT add overflow-y-auto
                // here; overscroll bounce on the panel breaks the clipping. Scrolling is handled
                // by the inner wrapper. flex + flex-col give the inner wrapper something to
                // stretch into so overflow-y-auto actually engages.
                'overflow-hidden flex flex-col bg-surface',
                isBottomSheet
                  ? // Bottom sheet: full-width (capped at max-w-lg), anchored to the bottom of
                    // the viewport with only the top corners rounded. max-h-[90dvh] leaves a
                    // ~10dvh gap at the top so the sheet never fully covers the screen; dvh
                    // (not vh) accounts for mobile browser chrome shrinking the viewport.
                    'max-w-lg max-h-[90dvh] rounded-t-large'
                  : // Centered dialog. dvh accounts for mobile browser chrome (address bar)
                    // shrinking the viewport. var(--spacing-4) = 1rem; the *2 gives 1rem of
                    // breathing room top and bottom, matching the p-4 on the backdrop container.
                    'max-w-sm sm:max-w-md lg:max-w-lg max-h-[calc(100dvh-2*var(--spacing-4))] rounded-large'
              )}
              variants={isBottomSheet ? bottomSheetVariants : panelVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {/* Inner scroll wrapper — takes the padding and scrolls as a unit.
                  Padding lives here (not on the panel) so the scrollbar sits flush
                  at the panel edge rather than inset from it. */}
              <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-5 p-5">
                {/* Drag-handle affordance (bottom-sheet only) */}
                {isBottomSheet && (
                  <div className="flex justify-center" aria-hidden="true">
                    <span className="h-1 w-10 rounded-rounded bg-disabled" />
                  </div>
                )}

                {/* Row 1: Close button */}
                <div className="flex justify-end">
                  <button
                    ref={closeButtonRef}
                    type="button"
                    onClick={onClose}
                    aria-label="Close modal"
                    className={cn(
                      'flex items-center justify-center',
                      'p-1 rounded-full border border-default',
                      'bg-transparent cursor-pointer',
                      'transition-colors duration-150',
                      'hover:bg-surface-hover',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                      'focus-visible:ring-(--border-color-interactiveloud)'
                    )}
                  >
                    <Icon name="close" size="large" />
                  </button>
                </div>

                {/* Content area */}
                {hasChildren ? (
                  <div>{(rest as ModalPropsWithChildren).children}</div>
                ) : (
                  <div className="flex flex-col items-center text-center gap-3">
                    {/* Row 2: Optional icon */}
                    {'icon' in rest && rest.icon && (
                      <div
                        className={cn(
                          'flex items-center justify-center',
                          'size-10 rounded-full bg-informative'
                        )}
                      >
                        <Icon name={rest.icon} size="large" />
                      </div>
                    )}

                    {/* Row 3: Title */}
                    <Typography id={titleId} variant="header" size="h1" as="h2">
                      {(rest as ModalPropsWithContent).title}
                    </Typography>

                    {/* Row 4: Description */}
                    <Typography variant="body" size="medium">
                      {(rest as ModalPropsWithContent).description}
                    </Typography>
                  </div>
                )}

                {/* Row 5+: Footer (checkbox + buttons) */}
                {(showDoNotShowAgain || hasButtons) && (
                  <div className="flex flex-col gap-5">
                    {/* Optional "Don't show this again" checkbox */}
                    {showDoNotShowAgain && (
                      <div className="flex justify-center">
                        <Checkbox
                          checked={doNotShowAgain}
                          onChange={setDoNotShowAgain}
                          label="Don't show this again"
                        />
                      </div>
                    )}

                    {/* Buttons */}
                    {hasButtons && (
                      <div className="flex flex-col gap-4">
                        {primaryButton && (
                          <Button
                            variant="primary"
                            fullWidth
                            onClick={handlePrimary}
                            isLoading={primaryButton.isLoading}
                            disabled={primaryButton.disabled}
                          >
                            {primaryButton.label}
                          </Button>
                        )}
                        {secondaryButton && (
                          <Button
                            variant="secondary"
                            fullWidth
                            onClick={handleSecondary}
                            isLoading={secondaryButton.isLoading}
                            disabled={secondaryButton.disabled}
                          >
                            {secondaryButton.label}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* end inner scroll wrapper */}
            </motion.div>
          </motion.div>
        </FocusTrap>
      )}
    </AnimatePresence>
  );

  const portalRoot = document.getElementById('modal-root') ?? document.body;
  return createPortal(modal, portalRoot);
}

export default Modal;
